import { Link, useLocation } from "wouter";
import { Icon } from "../../web/components/Icon";
import { ErrorNotice, Spinner } from "../../web/components/ui";
import { BundlesList } from "./BundlesList";
import { usePriceList } from "./data";
import { GroupsList } from "./GroupsList";
import { MaterialsList } from "./MaterialsList";
import { meta } from "./meta";
import { SettingsSection } from "./SettingsSection";
import { SuppliersList } from "./SuppliersList";
import { usePriceListWindows } from "./windows";

/*
 * The Price List's pages (under /t/pricelist): /materials (also the tool's own address), /groups, /bundles,
 * /suppliers and /settings. Clicking a record opens its window over the page (windows.ts; the
 * windows themselves are drawn by WindowHost.tsx, on any page).
 */

const BASE = `/t/${meta.id}`;
const SECTIONS = ["materials", "groups", "bundles", "suppliers", "settings"] as const;
type Section = (typeof SECTIONS)[number];

export default function PriceList() {
    const [location] = useLocation();
    const windows = usePriceListWindows();
    const { params, set } = windows;
    const { data, error, isLoading } = usePriceList();
    const first = location.slice(BASE.length).split("/").find(Boolean);
    const section: Section = SECTIONS.includes(first as Section) ? (first as Section) : "materials";

    if (isLoading) return <Spinner full />;
    if (!data) {
        return (
            <div className="page">
                <ErrorNotice error={error ?? new Error("The Price List couldn't be loaded.")} />
            </div>
        );
    }

    return (
        <>
            {/* On wide screens the sidebar lists these under the Price List (components/Shell.tsx). */}
            <nav className="tabs md:hidden" aria-label="Price List">
                {meta.sections?.map((s) => (
                    <Link
                        key={s.id}
                        href={`${BASE}/${s.id}` + (params.toString() ? "?" + params.toString() : "")}
                        aria-current={section === s.id ? "page" : undefined}
                        className="tab"
                    >
                        <Icon name={s.icon} className="size-4" />
                        {s.name}
                    </Link>
                ))}
            </nav>
            {section === "settings" && <SettingsSection data={data} />}
            {section === "materials" && (
                <MaterialsList data={data} openUuid={params.get("item") ?? undefined} onOpen={(uuid) => windows.openItem(uuid)} onNew={() => set({ item: "new", itemTab: null })} />
            )}
            {section === "groups" && <GroupsList data={data} openId={params.get("group") ?? undefined} onOpen={(id, options) => windows.openGroup(id, options)} />}
            {section === "bundles" && (
                <BundlesList data={data} openUuid={params.get("bundle") ?? undefined} onOpen={(uuid) => windows.openBundle(uuid)} onNew={() => set({ bundle: "new", bundleTab: null })} />
            )}
            {section === "suppliers" && (
                <SuppliersList
                    data={data}
                    openUuid={params.get("supplier") ?? undefined}
                    onOpen={(uuid) => windows.openSupplier(uuid)}
                    onNew={() => set({ supplier: "new", supplierTab: null })}
                />
            )}
        </>
    );
}
